import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { Spinner, SpinnerType } from '@nx-frontend/shared/ui/spinner';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { AIAgent, PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { uploadFile } from '@nx-frontend/shared/utils/upload-file';
import { AgentModeForm } from './agent-mode-form';
import { useGetInvalidFields } from './agent-mode/get-invalid-fields';
import {
  $aiHelperAgent,
  $chatbotAgentModeMainTabState,
  AgentModeMainTabState,
  abortAgentMessageEvent,
  createAIHelperAgentThreadFx,
  createAgentMessageEvent,
  setChatbotAgentModeMainTabStateEvent,
} from './agent-store';
import { ChatbotAgentStartView } from './chatbot-agent-start-view';
import { ChatbotChat } from './chatbot-chat';
import { ChatbotContext } from './chatbot-context';
import { ChatbotInput } from './chatbot-input';
import {
  $activeThreadId,
  $aiThreads,
  $writingMessage,
  setActiveThreadIdEvent,
  onMessageResolvedEvent,
  updateAiAgentThreadMessagesEvent,
} from './store';
import { AgentInput, ChatBotAttachedFile } from './types';
import { useChatbotFileUpload } from './use-chatbot-file-upload';
import { isTextInputNode } from './util';

type ChatbotAgentModeMainTabProps = {
  agent?: AIAgent;
  agentLoaded?: boolean;
  openExistingAgentThread?: boolean;
  noInputLineMode?: boolean;
  startWithStartScreen?: boolean;
};

export const ChatbotAgentModeMainTab: FC<ChatbotAgentModeMainTabProps> = (props: ChatbotAgentModeMainTabProps) => {
  const [aiHelperAgent, chatbotAgentModeMainTabState, activeThreadId, aiThreads, writingMessage, currentWorkspace] =
    useUnit([
      $aiHelperAgent,
      $chatbotAgentModeMainTabState,
      $activeThreadId,
      $aiThreads,
      $writingMessage,
      $currentWorkspace,
    ]);

  const [
    setActiveThreadId,
    createAgentMessage,
    onMessageResolved,
    updateAiAgentThreadMessages,
    abortAgentMessage,
    setChatbotAgentModeMainTabState,
  ] = useUnit([
    setActiveThreadIdEvent,
    createAgentMessageEvent,
    onMessageResolvedEvent,
    updateAiAgentThreadMessagesEvent,
    abortAgentMessageEvent,
    setChatbotAgentModeMainTabStateEvent,
  ]);

  const { agent, agentLoaded, openExistingAgentThread } = props;
  const aiContext = useContext(ChatbotContext);

  const [input, setInput] = useState<Record<string, string | File>>({});
  const [inputsWithErrors, setInputsWithErrors] = useState<{ id: string; error: string }[]>([]);

  const chatbotInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [creatingThreadInProgress, setCreatingThreadInProgress] = useState(false);

  const onInputFieldsChange = useCallback((fields: Record<string, string | File>) => {
    setInput(fields);
  }, []);

  const t = useTranslations('features');

  let inputPlaceholderText = activeThreadId
    ? t('ai-helper.tell_what_to_do_next')
    : t('ai-helper.start_a_chat') + ' ' + t('ai-helper.use_to_add_context');
  if (aiContext.portalId) {
    inputPlaceholderText = t('ai-helper.ask_a_question');
    if (!aiContext.isVisitor) {
      inputPlaceholderText += ' ' + t('ai-helper.use_to_add_context');
    }
  }

  const aiIsWritingMessageInCurrentThread = useMemo(() => {
    return (
      Boolean(aiThreads.tmpMessages?.length) ||
      (activeThreadId !== null && Boolean(aiThreads.threads[activeThreadId]?.isWriting))
    );
  }, [activeThreadId, aiThreads.threads, aiThreads.tmpMessages?.length]);

  useEffect(() => {
    if (!aiIsWritingMessageInCurrentThread) {
      // Need to focus on the assistant input box on assistant popup open and when AI finishes writing.
      // However, we don't want to focus on the input box if the user is typing in another editable element.
      if (!isTextInputNode(document.activeElement as HTMLElement)) {
        if (chatbotInputRef.current) {
          chatbotInputRef.current.focus();
        }
      }
    }
  }, [aiIsWritingMessageInCurrentThread]);

  const onGenerateResponse = useCallback(async () => {
    setCreatingThreadInProgress(true);

    const inputWithFilesSaved: AgentInput = {};
    // save files that are in the input as tempStoredFiles
    for (const key in input) {
      if (input[key] instanceof File) {
        const tempStoredFile = await uploadFile(input[key] as File, {
          folder: 'ai_agent',
        });
        if (tempStoredFile) {
          inputWithFilesSaved[key] = {
            filename: tempStoredFile.filename,
            name: tempStoredFile.name,
          };
        }
        // const file = input[key] as File;
      } else {
        inputWithFilesSaved[key] = input[key] as string;
      }
    }

    createAIHelperAgentThreadFx({
      orgId: aiContext.orgId as string,
      agentId: aiHelperAgent.agent?.globalId as string,
      input: inputWithFilesSaved,
      workspaceId: aiContext.workspaceId as string,
    })
      .then(({ id }) => {
        setActiveThreadId(id);
        const abortController = new AbortController();
        createAgentMessage({
          apiHost: aiContext.apiHostCreateMessage,
          sessionId: aiContext.sessionId,
          orgId: aiContext.orgId as string,
          threadId: id,
          workspaceId: aiContext.workspaceId as string,
          text: null,
          abortController,
          onMessageChunkResolved: onMessageResolved,
          webPageDetails: aiContext.webPageDetails,
        });
      })
      .catch((err) => {
        // TODO: handle error
        console.error(err);
      })
      .finally(() => {
        setCreatingThreadInProgress(false);
      });
  }, [
    aiContext.orgId,
    aiHelperAgent.agent?.globalId,
    input,
    aiContext.workspaceId,
    setActiveThreadId,
    createAgentMessage,
    onMessageResolved,
    aiContext.apiHostCreateMessage,
    aiContext.sessionId,
    aiContext.webPageDetails,
  ]);

  const inputsExist = useMemo(() => {
    if (!agentLoaded) {
      return true;
    }
    return Boolean(agent && agent?.inputs.length);
  }, [agent, agentLoaded]);

  // Effect for running agents automatically if no start screen and inputs
  const autoRunTriggered = useRef(false);
  useEffect(() => {
    if (agentLoaded && !props.startWithStartScreen && !inputsExist && !autoRunTriggered.current) {
      autoRunTriggered.current = true;
      onGenerateResponse();
    }
  }, [inputsExist, agentLoaded, onGenerateResponse, props.startWithStartScreen]);

  const { getRootProps, getInputProps, open, files, removeFile, resetFiles, isDragActive } = useChatbotFileUpload();

  const onRemoveFile = useCallback(
    (file: ChatBotAttachedFile) => {
      removeFile(file);
    },
    [removeFile]
  );

  const isFilesUploading = useMemo(() => {
    return files.some((file) => file.uploadState === 'uploading');
  }, [files]);

  const onSendMessage = useCallback(
    (text: string) => {
      if (isFilesUploading) {
        return;
      }

      const abortController = new AbortController();

      if (!activeThreadId) {
        return console.error('Sending agent message without active thread id');
      }

      const filesToCreateMessage = files.filter((file) => Boolean(file.tempStoredFileName));

      resetFiles();

      createAgentMessage({
        apiHost: aiContext.apiHostCreateMessage,
        sessionId: aiContext.sessionId,
        orgId: aiContext.orgId as string,
        threadId: activeThreadId,
        workspaceId: aiContext.workspaceId as string,
        text,
        abortController,
        onMessageChunkResolved: onMessageResolved,
        files: filesToCreateMessage,
        webPageDetails: aiContext.webPageDetails,
      });
    },
    [
      activeThreadId,
      aiContext.sessionId,
      aiContext.orgId,
      aiContext.workspaceId,
      createAgentMessage,
      onMessageResolved,
      aiContext.apiHostCreateMessage,
      files,
      resetFiles,
      isFilesUploading,
      aiContext.webPageDetails,
    ]
  );

  const getInvalidFields = useGetInvalidFields({
    agent,
    input,
  });

  useEffect(() => {
    if (agentLoaded) {
      if (!agent) {
        setChatbotAgentModeMainTabState(AgentModeMainTabState.AGENT_NOT_FOUND);
        return;
      }
      if (aiHelperAgent.state === 'error') {
        setChatbotAgentModeMainTabState(AgentModeMainTabState.ERROR);
        return;
      }
      if (activeThreadId) {
        setChatbotAgentModeMainTabState(AgentModeMainTabState.CHAT);
        return;
      }
      if (props.startWithStartScreen) {
        setChatbotAgentModeMainTabState(AgentModeMainTabState.START_VIEW);
        return;
      } else if (!inputsExist) {
        setChatbotAgentModeMainTabState(AgentModeMainTabState.LOADING);
        return;
      } else if (inputsExist) {
        setChatbotAgentModeMainTabState(AgentModeMainTabState.INPUT);
        return;
      }
      setChatbotAgentModeMainTabState(AgentModeMainTabState.CHAT);
    } else {
      setChatbotAgentModeMainTabState(AgentModeMainTabState.LOADING);
    }
  }, [
    inputsExist,
    onGenerateResponse,
    agentLoaded,
    props.startWithStartScreen,
    activeThreadId,
    aiHelperAgent.state,
    agent,
    setChatbotAgentModeMainTabState,
  ]);

  // If opening existing thread, load its messages
  // TODO: show loading state while messages are being fetched
  useEffect(() => {
    if (openExistingAgentThread && activeThreadId) {
      updateAiAgentThreadMessages({
        apiHost: aiContext.apiHost,
        threadId: activeThreadId,
        orgId: currentWorkspace.orgId,
      });
    }
  }, [activeThreadId, aiContext.apiHost, currentWorkspace.orgId, openExistingAgentThread, updateAiAgentThreadMessages]);

  const onRunTaskClick = useCallback(() => {
    if (inputsExist) {
      setChatbotAgentModeMainTabState(AgentModeMainTabState.INPUT);
    } else {
      onGenerateResponse();
    }
  }, [inputsExist, onGenerateResponse, setChatbotAgentModeMainTabState]);

  const onGenerateClick = useCallback(() => {
    const invalidFields = getInvalidFields();
    if (invalidFields && invalidFields.length) {
      setInputsWithErrors(invalidFields);
    } else {
      setInputsWithErrors([]);
      onGenerateResponse();
    }
  }, [getInvalidFields, onGenerateResponse]);

  const onAbortMessage = useCallback(
    (messageId: string, text: string) => {
      if (!aiContext.orgId) {
        return console.error('No orgId provided, fail to cancel agent message');
      }
      abortAgentMessage({
        sessionId: aiContext.sessionId,
        orgId: aiContext.orgId,
        messageId,
        text,
      });
    },
    [abortAgentMessage, aiContext.sessionId, aiContext.orgId]
  );

  if (chatbotAgentModeMainTabState === AgentModeMainTabState.LOADING) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (chatbotAgentModeMainTabState === AgentModeMainTabState.ERROR) {
    return <div>Fail to load agent.</div>;
  }

  if (chatbotAgentModeMainTabState === AgentModeMainTabState.AGENT_NOT_FOUND) {
    return <div className="text-center mt-5">Agent not found</div>;
  }

  let content;

  if (agent && chatbotAgentModeMainTabState === AgentModeMainTabState.START_VIEW) {
    content = <ChatbotAgentStartView agent={agent} onRunClick={onRunTaskClick} />;
  }

  if (chatbotAgentModeMainTabState === AgentModeMainTabState.CHAT) {
    content = (
      <ChatbotChat
        disableLoading={true}
        threadId={activeThreadId}
        onSendMessage={onSendMessage}
        themeColors={aiContext.themeColors as ThemeColorSchemas}
        themeStyle={aiContext.themeStyle as PortalStyleConfig}
        aiName={'Agent'}
        aiCustomLogoUrl={null}
        isWriting={aiIsWritingMessageInCurrentThread}
        onAddMessageToPage={() => {
          /* void */
        }}
        onFeedback={() => {
          /* void */
        }}
        allowCreatePage={false}
        isAgent={true}
      />
    );
  }

  if (agent && chatbotAgentModeMainTabState === AgentModeMainTabState.INPUT) {
    content = (
      <div className="overflow-y-auto flex flex-col h-full pt-5">
        <div className="rounded-lg border border-[zinc-100] bg-[radial-gradient(191.81%_165.86%_at_70.71%_25.88%,_#FAFAFA_0%,_#F0FDFA_100%)] p-4 flex flex-col ml-6 mr-6 mb-6 mt-auto">
          <div className="text-[16px] leading-[148%] text-neutral-800">{agent.description}</div>
          <AgentModeForm onFieldsChange={onInputFieldsChange} inputsWithErrors={inputsWithErrors} />

          <div className="flex justify-center mt-[18px]">
            <Button
              className="flex justify-center gap-2"
              type={ButtonType.FLAT}
              size={ButtonSize.MD}
              onClick={onGenerateClick}
            >
              Generate
              {creatingThreadInProgress && <Spinner width={14} height={14} type={SpinnerType.WHITE} />}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return !agent ? null : (
    <div
      className="flex flex-col h-full"
      {...(chatbotAgentModeMainTabState === AgentModeMainTabState.CHAT ? getRootProps() : {})}
    >
      {chatbotAgentModeMainTabState === AgentModeMainTabState.CHAT && <input {...getInputProps()} />}
      {isDragActive && (
        <div className="absolute rounded-xl inset-0 z-50 bg-gray-600/10 backdrop-saturate-150 flex items-center justify-center">
          <div className="text-center flex flex-col items-center">
            <Icon icon={IconsMap.FILE} className="w-10 h-10" />
            <p className="text-lg font-medium">Drop file here to add to conversation</p>
          </div>
        </div>
      )}
      {content}
      {!props.noInputLineMode && activeThreadId && chatbotAgentModeMainTabState === AgentModeMainTabState.CHAT && (
        <ChatbotInput
          sendDisabled={isFilesUploading}
          chosenFiles={files}
          fileUpload={true}
          chooseFile={open}
          inputRef={chatbotInputRef}
          themeColors={aiContext.themeColors as ThemeColorSchemas}
          themeStyle={aiContext.themeStyle as PortalStyleConfig}
          onSend={onSendMessage}
          isWriting={aiIsWritingMessageInCurrentThread}
          isCreatingPage={
            aiIsWritingMessageInCurrentThread &&
            activeThreadId &&
            aiThreads.threads[activeThreadId].messages &&
            writingMessage?.system
              ? true
              : false
          }
          useWeb={false}
          onSwitchUseWeb={() => {
            /* void */
          }}
          onAbortMessage={onAbortMessage}
          placeholder={inputPlaceholderText}
          onRemoveFile={onRemoveFile}
        />
      )}
    </div>
  );
};
